// Pure, dependency-free trace sampler for the Vite/React SPAs (backoffice, admin).
//
// Replaces the flat 0.1 tracesSampleRate: noise transactions cost quota and carry no signal,
// while money paths are low-volume and the ones we most want a trace for when something breaks.

/** The slice of Sentry's SamplingContext this sampler reads (kept structural: no @sentry import). */
export interface TraceSamplingContext {
  name?: string | undefined;
  parentSampled?: boolean | undefined;
}

export const DEFAULT_TRACE_SAMPLE_RATE = 0.1;
export const PAYMENT_TRACE_SAMPLE_RATE = 1;

// Health/readiness probes, static assets, framework internals and the runtime-config script.
const NOISE_TRANSACTION =
  /(^|[\s/])(healthz?|readyz?|readiness|livez?|ping|favicon\.ico|robots\.txt|config\.js)([/?\s]|$)|\/_next\/|\/static\/|\/assets\/|\.(?:js|mjs|css|map|png|jpe?g|gif|svg|ico|woff2?)(?:[?\s]|$)/i;

// Money routes. `\b` treats `/` and `-` as boundaries, so this covers /pay, /payments,
// /checkout, pay-link, pay-order, pay-invoice, pay-account and guest-pay without matching payroll.
const PAYMENT_TRANSACTION = /\b(pay|payments?|checkout)\b/i;

/** True for transactions that are never worth a trace (probes, static assets, config.js). */
export function isNoiseTransaction(name: string): boolean {
  return NOISE_TRANSACTION.test(name);
}

/** True for payment / checkout / pay-link / guest-pay style transactions. */
export function isPaymentTransaction(name: string): boolean {
  return PAYMENT_TRANSACTION.test(name);
}

/**
 * Sentry `tracesSampler`: 0 for noise, 1.0 for payment routes, otherwise the parent's decision when
 * there is one (keeps distributed traces whole) and 0.1 for fresh traces.
 */
export function tracesSampler(ctx: TraceSamplingContext): number {
  const name = ctx.name ?? '';
  if (isNoiseTransaction(name)) return 0;
  if (isPaymentTransaction(name)) return PAYMENT_TRACE_SAMPLE_RATE;
  if (typeof ctx.parentSampled === 'boolean') return ctx.parentSampled ? 1 : 0;
  return DEFAULT_TRACE_SAMPLE_RATE;
}
